import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Button, Container, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useOutletContext } from "react-router-dom";
import type { Simplify } from "type-fest";

import type { GuestAssessment, GuestSession } from "@thalamos/haven-common";

import guestApi from "../../api/guest/api.js";
import { guestSignOutUrl } from "../../api/user/api.js";
import { guestRoute } from "../../routing/guestRoute.js";
import {
  AssessmentFormProvider,
  type AssessmentRead,
  type FormClient,
} from "../Assessment/AssessmentContext.js";
import {
  AssessmentFrameProvider,
  type AssessmentFrame,
} from "../Assessment/AssessmentFrame.js";
import { GuestBrand } from "./GuestBrand.js";

const { guest } = guestApi;

const refusedForAGuest = () =>
  Promise.reject(new Error("A guest's draft carries nothing over"));

/**
 * The guest session's routes for its one assessment. A guest's draft always
 * starts blank, so nothing is carried over and no domain has to be opened
 * first: the calls for those refuse. A save the session no longer admits
 * calls `onSignedOut`, since every later save would be refused too.
 */
export const guestFormClientFor = (onSignedOut: () => void): FormClient => ({
  instrumentSchema: () => guest.instrument(),
  formDraft: {
    create: () => guest.startDraft(),
    resume: () => guest.resumeDraft(),
    save: async (_assessmentId, body) => {
      const saved = await guest.saveDraft(body);
      if (saved.status === 401) {
        onSignedOut();
      }
      return saved;
    },
    previousComments: refusedForAGuest,
    openGroup: refusedForAGuest,
  },
});

/** A guest session reaches the one assessment it owns, so whoever has it open answers it. */
export const guestAssessmentFrame: AssessmentFrame = {
  paths: {
    overview: () => guestRoute.assessment(),
    domain: (_assessmentId, group) => guestRoute.assessmentDomain(group),
    item: (_assessmentId, group, item) =>
      guestRoute.assessmentItem(group, item),
    away: () => undefined,
  },
  isOwner: () => true,
};

/** What a guest's screens share beside the assessment: the session, the asker's message and the day access ends. */
export type GuestAssessmentDetails = Simplify<
  { session: GuestSession } & Pick<GuestAssessment, "message" | "expiresAt">
>;

/** The details the surrounding `GuestAssessmentLayout` read. */
export const useGuestAssessmentDetails = (): GuestAssessmentDetails =>
  useOutletContext<GuestAssessmentDetails>();

/**
 * Reading what a guest session reaches: under way, read, ended (the session
 * no longer admits the guest), or failed for another reason.
 */
export type GuestRead =
  | { kind: "reading" }
  | { kind: "read"; session: GuestSession; assessment: GuestAssessment }
  | { kind: "ended" }
  | { kind: "failed" };

const readGuest = async (): Promise<GuestRead> => {
  const [session, assessment] = await Promise.all([
    guest.session(),
    guest.assessment(),
  ]);

  if (session.status === 401 || assessment.status === 401) {
    return { kind: "ended" };
  }

  return session.data === undefined || assessment.data === undefined
    ? { kind: "failed" }
    : { kind: "read", session: session.data, assessment: assessment.data };
};

/**
 * Haven's mark and name over a guest's page, and nothing else of Haven but
 * Sign out, so a guest on a shared computer can end their session before
 * walking away.
 */
export const GuestBrandedPage = ({
  children,
  offersSignOut = true,
}: {
  children: ReactNode;
  /** False where the page says the guest is already signed out. */
  offersSignOut?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <GuestBrand />
          {offersSignOut ? (
            <Button size="small" href={guestSignOutUrl()}>
              {t("auth.signOut")}
            </Button>
          ) : null}
        </Stack>
        {children}
      </Stack>
    </Container>
  );
};

/** Where a guest lands once they have chosen Sign out. */
export const GuestSignedOutPage = () => {
  const { t } = useTranslation();

  return (
    <GuestBrandedPage offersSignOut={false}>
      <Typography variant="h4" component="h1">
        {t("guestAccess.signedOut.heading")}
      </Typography>
      <Typography>{t("guestAccess.signedOut.body")}</Typography>
    </GuestBrandedPage>
  );
};

/**
 * The frame around a guest's screens of the assessment they were asked for:
 * no banner naming the person, no way back to a profile, no lists, tabs or search.
 * Holds the assessment open through the guest session's routes, for the
 * screens inside it to share.
 */
export const GuestAssessmentLayout = () => {
  const [read, setRead] = useState<GuestRead>({ kind: "reading" });
  const [client] = useState(() =>
    guestFormClientFor(() => {
      setRead({ kind: "ended" });
    }),
  );

  // A read that fails keeps the assessment as last read, so the form, and
  // the answers in it, stay with the guest; it rejects, as `reread` must.
  const reread = useCallback(async () => {
    const next = await readGuest().catch((): GuestRead => ({ kind: "failed" }));
    setRead((was) =>
      next.kind === "failed" && was.kind === "read" ? was : next,
    );

    if (next.kind === "failed") {
      throw new Error("The guest's assessment could not be read");
    }
  }, []);

  useEffect(() => {
    // The failure is shown by the read state the call set.
    reread().catch(() => undefined);
  }, [reread]);

  return (
    <GuestAssessmentLayoutContent read={read} reread={reread} client={client} />
  );
};

/**
 * The layout's rendering, with what was read injected so a story or a test
 * can show any state of it without an API client.
 */
export const GuestAssessmentLayoutContent = ({
  read,
  reread,
  client,
}: {
  read: GuestRead;
  reread: () => Promise<void>;
  /** What the form is opened and saved through; see `guestFormClientFor`. */
  client: FormClient;
}) => {
  const { t } = useTranslation();

  if (read.kind === "ended") {
    return (
      <GuestBrandedPage offersSignOut={false}>
        <Typography variant="h4" component="h1">
          {t("guestAccess.sessionEnded.heading")}
        </Typography>
        <Typography>{t("guestAccess.sessionEnded.body")}</Typography>
      </GuestBrandedPage>
    );
  }

  const assessmentRead: AssessmentRead =
    read.kind === "read"
      ? { kind: "read", assessment: read.assessment.assessment }
      : read.kind === "failed"
        ? { kind: "failed" }
        : { kind: "reading" };

  return (
    <GuestBrandedPage>
      <AssessmentFrameProvider value={guestAssessmentFrame}>
        <AssessmentFormProvider
          read={assessmentRead}
          reread={reread}
          client={client}
        >
          {read.kind === "read" ? (
            <Outlet
              context={
                {
                  session: read.session,
                  message: read.assessment.message,
                  expiresAt: read.assessment.expiresAt,
                } satisfies GuestAssessmentDetails
              }
            />
          ) : read.kind === "failed" ? (
            <Typography color="error">
              {t("assessment.page.failed.unavailable")}
            </Typography>
          ) : (
            <Typography color="text.secondary">
              {t("common.loading")}
            </Typography>
          )}
        </AssessmentFormProvider>
      </AssessmentFrameProvider>
    </GuestBrandedPage>
  );
};
